/**
 * How much law a register holds for a matter, and the breakdown panel behind it.
 *
 * A dot and a count, not a bar. The bar this replaced was a fixed track split between superseded
 * and in force, and those two always sum to the whole — so it was **completely full for every
 * register that held anything at all**. A register holding one rule and a register holding four
 * hundred were the same picture, and a filled horizontal track in a list reads as coverage. The
 * only honest denominator would be "how many rules this register ought to have", which nothing
 * knows.
 *
 * So the count is stated and the dot answers the one question the engine can answer: does this
 * register currently return anything for this matter?
 *
 * `summary` is `MatterSummary | null`, not a count: "Not covered" renders only for `null`, and a
 * covered matter with zero versions gets its own state. "The EPO does not register trade marks"
 * and "we have not researched this yet" are different facts, and keying on `total === 0`
 * conflates them (`contradictions.md` #7).
 */

import { useId, useRef, useState } from 'react';
import { AnchoredPanel } from '~/ui/AnchoredPanel';
import { Swatch } from '~/ui/Surface';
import { fmtYear, plural } from '~/lib/format';
import type { JurisdictionRow, MatterSummary, VocabularyTerm } from '~/api/schema';
import styles from './Register.module.css';

export interface StackedRuleBarProps {
  label: string;
  summary: MatterSummary | null;
}

export function StackedRuleBar({ label, summary }: StackedRuleBarProps) {
  if (summary === null) {
    return (
      <div className={styles.barRow}>
        <span className={styles.barLabel}>{label}</span>
        <span className={styles.notCovered}>Not covered</span>
      </div>
    );
  }

  if (summary.versions === 0) {
    return (
      <div className={styles.barRow}>
        <span className={styles.barLabel}>{label}</span>
        <span className={styles.noVersions}>No rule texts yet</span>
      </div>
    );
  }

  // Red where the register holds rules and none of them currently governs. That is not the same
  // as holding nothing, and it is the state worth seeing from a list: every answer this register
  // gives for today's filings comes from a chain that has run out.
  const answering = summary.inForce > 0;
  const superseded = summary.superseded > 0 ? `, ${summary.superseded} superseded` : '';

  return (
    <div className={styles.barRow}>
      <span className={styles.barLabel}>{label}</span>
      <span
        className={styles.state}
        aria-label={`${label}: ${plural(summary.rules, 'rule')}, ${plural(summary.versions, 'text')}, ${summary.inForce} in force${superseded}`}
      >
        <span
          className={[styles.dot, answering ? styles.answering : styles.silent].join(' ')}
          aria-hidden="true"
        />
        <span className={styles.count}>{plural(summary.rules, 'rule')}</span>
      </span>
    </div>
  );
}

export interface MatterBarGroupProps {
  row: JurisdictionRow;
  matters: readonly VocabularyTerm[];
  /** Makes the group the anchor for the breakdown panel. */
  interactive?: boolean;
}

export function MatterBarGroup({ row, matters, interactive = false }: MatterBarGroupProps) {
  const anchorRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const panelId = useId();

  const bars = (
    <div className={styles.bars}>
      {matters.map((matter) => (
        <StackedRuleBar
          key={matter.code}
          label={matter.label}
          summary={row.matters.find((entry) => entry.matter === matter.code) ?? null}
        />
      ))}
    </div>
  );

  if (!interactive) return bars;

  return (
    <>
      <button
        ref={anchorRef}
        type="button"
        className={styles.barCell}
        aria-describedby={open ? panelId : undefined}
        aria-expanded={open}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
      >
        {bars}
      </button>
      <AnchoredPanel id={panelId} anchorRef={anchorRef} open={open} onDismiss={() => setOpen(false)}>
        <RuleTextBreakdown row={row} matters={matters} />
      </AnchoredPanel>
    </>
  );
}

/**
 * The figures behind the bars, as a readable table rather than a hint: the figures are the point
 * of the interaction, and the column headers carry the same swatches as the bar so the panel is
 * self-keying.
 */
export function RuleTextBreakdown({
  row,
  matters,
}: {
  row: JurisdictionRow;
  matters: readonly VocabularyTerm[];
}) {
  const held = matters.map((matter) => ({
    matter,
    summary: row.matters.find((entry) => entry.matter === matter.code) ?? null,
  }));

  const totals = held.reduce(
    (sum, entry) => ({
      rules: sum.rules + (entry.summary?.rules ?? 0),
      versions: sum.versions + (entry.summary?.versions ?? 0),
      superseded: sum.superseded + (entry.summary?.superseded ?? 0),
      inForce: sum.inForce + (entry.summary?.inForce ?? 0),
    }),
    { rules: 0, versions: 0, superseded: 0, inForce: 0 },
  );

  return (
    <>
      <div className={styles.breakdownHead}>
        <p className={styles.breakdownTitle}>{row.name}</p>
        <p className={styles.breakdownSub}>
          {row.registry} · {row.status === 'active' ? 'Active' : 'Dead'}
        </p>
      </div>
      <div className={styles.breakdownBody}>
        <table className={styles.breakdownTable}>
          <thead>
            <tr>
              <th scope="col">Matter</th>
              <th scope="col" className={styles.numeric}>
                Rules
              </th>
              <th scope="col" className={styles.numeric}>
                Texts
              </th>
              <th scope="col" className={styles.numeric}>
                <Swatch tone="superseded" /> Past
              </th>
              <th scope="col" className={styles.numeric}>
                <Swatch tone="inforce" /> Live
              </th>
            </tr>
          </thead>
          <tbody>
            {held.map(({ matter, summary }) =>
              summary === null ? (
                <tr key={matter.code} className={styles.breakdownAbsent}>
                  <td className={styles.label}>{matter.label}</td>
                  <td colSpan={4}>Not covered</td>
                </tr>
              ) : (
                <tr key={matter.code}>
                  <td className={styles.label}>{matter.label}</td>
                  <td className={styles.numeric}>{summary.rules}</td>
                  <td className={styles.numeric}>{summary.versions}</td>
                  <td className={styles.numeric}>{summary.superseded}</td>
                  <td className={styles.numeric}>{summary.inForce}</td>
                </tr>
              ),
            )}
            <tr className={styles.breakdownTotal}>
              <td>Total</td>
              <td className={styles.numeric}>{totals.rules}</td>
              <td className={styles.numeric}>{totals.versions}</td>
              <td className={styles.numeric}>{totals.superseded}</td>
              <td className={styles.numeric}>{totals.inForce}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div className={styles.breakdownFoot}>
        Opened {row.founded ?? '—'}
        {row.closedOn ? ` · closed ${fmtYear(row.closedOn)}` : ''}
      </div>
    </>
  );
}
